"use client";

import { useMemo } from "react";

import { useAppAbility } from "@/features/auth/casl";
import { useActiveWorkspaceSlug } from "@/features/auth/hooks/use-active-workspace-slug";
import { useAdminRouteSlugs } from "@/features/auth/hooks/use-admin-route-slugs";
import {
  resolveTenantAuthorizationDecision,
  resolveTenantDepartmentAuthorizationDecision,
} from "@/features/auth/tenant-authorization/lib/tenant-authorization-registry";
import { WorkspaceAccessModule, WorkspaceAccessPermission } from "@/features/auth/model/auth-types";
import { recordTenantAuthorizationDecision } from "@/features/auth/tenant-authorization/lib/tenant-authorization-route-diagnostics";
import {
  type TenantAuthorizationDecision,
  type TenantAuthorizationInput,
  TenantAuthorizationAction,
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types";
import { resolveWorkspaceAccessRecord } from "@/features/auth/lib/workspace-access-record";
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider";

/**
 * Tenant UI's single authorization entry point. Components pass a typed operation; this hook owns
 * workspace lookup, route context, CASL evaluation, and the loading/allowed/denied lifecycle.
 */
export function useTenantAuthorization() {
  const ability = useAppAbility();
  const { authMe, authMeResolved, isLoading } = useTenantAuthFlow();
  const { workspaceSlug: routeWorkspaceSlug, branchSlug } = useAdminRouteSlugs();
  const activeWorkspaceSlug = useActiveWorkspaceSlug();

  return useMemo(() => {
    const workspaceSlug = routeWorkspaceSlug ?? activeWorkspaceSlug;
    const input: TenantAuthorizationInput = {
      access: resolveWorkspaceAccessRecord(authMe, workspaceSlug),
      ability,
      requestedBranchSlug: branchSlug,
      isResolving: isLoading || !authMeResolved,
    };
    const resolveDecision = (
      action: TenantAuthorizationAction,
      requestedBranchSlugOverride: string | null | undefined,
    ): TenantAuthorizationDecision => {
      const decision = resolveTenantAuthorizationDecision(
        requestedBranchSlugOverride === undefined
          ? input
          : { ...input, requestedBranchSlug: requestedBranchSlugOverride },
        action,
      );
      return decision;
    };
    const route = typeof window === "undefined" ? null : window.location.pathname;
    const recordDecision = (
      operation: "routeDecision" | "can" | "filterNavigation",
      action: TenantAuthorizationAction,
      decision: TenantAuthorizationDecision,
      requestedBranchSlugOverride?: string | null,
    ) => {
      recordTenantAuthorizationDecision({
        operation,
        identifier: action,
        route,
        requestedBranchSlug: requestedBranchSlugOverride === undefined
          ? branchSlug ?? null
          : requestedBranchSlugOverride,
        decision,
      });
    };
    const routeDecision = (action: TenantAuthorizationAction, requestedBranchSlugOverride?: string | null) => {
      const decision = resolveDecision(action, requestedBranchSlugOverride);
      recordDecision("routeDecision", action, decision, requestedBranchSlugOverride);
      return decision;
    };
    const departmentDecision = (
      module: WorkspaceAccessModule,
      permission: WorkspaceAccessPermission,
      branchId: string | null | undefined,
      departmentId: string | null | undefined,
    ) => {
      const decision = resolveTenantDepartmentAuthorizationDecision(input, module, permission, branchId, departmentId);
      recordDecision("can", decision.action, decision);
      return decision;
    };
    return {
      routeDecision,
      departmentDecision,
      can: (action: TenantAuthorizationAction) => {
        const decision = resolveDecision(action, undefined);
        recordDecision("can", action, decision);
        return decision.status === TenantAuthorizationDecisionStatus.Allowed;
      },
      filterNavigation: <T>(items: readonly T[], actionForItem: (item: T) => TenantAuthorizationAction): T[] => {
        return items.filter((item) => {
          const action = actionForItem(item);
          const decision = resolveDecision(action, undefined);
          recordDecision("filterNavigation", action, decision);
          return decision.status === TenantAuthorizationDecisionStatus.Allowed;
        });
      },
    };
  }, [ability, activeWorkspaceSlug, authMe, authMeResolved, branchSlug, isLoading, routeWorkspaceSlug]);
}
